import { useCallback, useEffect, useRef, useState } from 'react';
import { adminAgentService } from '../../../../services/adminAgent.service';

/** Wait before asking for a new ticket after the socket closes, so a flapping link cannot spin. */
const RENEW_DEBOUNCE_MS = 1000;

/**
 * One-time ticket for the chat WebSocket URL, in place of the access token (which must not
 * appear in a URL). Every handshake spends a ticket, so a new one is minted whenever the socket
 * closes; the REST call carries the normal bearer token and the usual refresh handling.
 */
export function useAdminSocketTicket(conversationPublicId: string | undefined) {
  const [ticket, setTicket] = useState<string | null>(null);
  const mintingRef = useRef(false);
  const openRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mint = useCallback(async () => {
    if (!conversationPublicId || mintingRef.current) return;
    mintingRef.current = true;
    try {
      const next = await adminAgentService.createSocketTicket(conversationPublicId);
      // A ticket swapped in while the socket is open would force a needless reconnect.
      if (!openRef.current) setTicket(next.ticket);
    } catch {
      setTicket(null);
    } finally {
      mintingRef.current = false;
    }
  }, [conversationPublicId]);

  useEffect(() => {
    void mint();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [mint]);

  const handleOpen = useCallback(() => {
    openRef.current = true;
  }, []);

  const handleClose = useCallback(() => {
    openRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void mint(), RENEW_DEBOUNCE_MS);
  }, [mint]);

  return { ticket, handleOpen, handleClose };
}
